import { AttendeApp } from './app';
import type { BootOptions, IdentifyOptions, AttendEvent } from './types';

(function () {
  'use strict';

  // Fila de comandos feitos antes do script carregar. O snippet oficial (admin → Widget) cria
  // `window.attende` como função com a fila em `.q` (lista de `arguments`); versões antigas
  // usavam `window.attendeQueue`. Aceitamos as duas.
  const w = window as unknown as Record<string, unknown>;
  const stub = w['attende'] as { q?: ArrayLike<unknown>[] } | undefined;
  const stubQueue: unknown[][] = Array.isArray(stub?.q) ? stub.q.map((args) => Array.prototype.slice.call(args) as unknown[]) : [];
  const legacyQueue: unknown[][] = Array.isArray(w['attendeQueue']) ? (w['attendeQueue'] as unknown[][]) : [];
  const queue: unknown[][] = [...legacyQueue, ...stubQueue];
  let app: AttendeApp | null = null;
  let booting = false;

  function isBootOptions(v: unknown): v is BootOptions {
    return typeof v === 'object' && v !== null && typeof (v as Record<string, unknown>)['tenant'] === 'string';
  }

  function isIdentifyOptions(v: unknown): v is IdentifyOptions {
    return typeof v === 'object' && v !== null && typeof (v as Record<string, unknown>)['userId'] === 'string';
  }

  function isAttendEvent(v: unknown): v is AttendEvent {
    return v === 'ready' || v === 'open' || v === 'close' || v === 'message' || v === 'unread';
  }

  function processCommand(args: unknown[]): void {
    const [cmd, ...rest] = args;

    switch (cmd) {
      case 'boot': {
        const opts = rest[0];
        if (!isBootOptions(opts)) {
          console.warn('[attende] boot: invalid options');
          return;
        }
        if (!booting && !app) {
          booting = true;
          app = new AttendeApp(opts);
          app.boot().catch((err: unknown) => {
            console.warn('[attende] Boot failed:', err);
          });
        }
        break;
      }
      case 'open':
        app?.openPanel().catch((err: unknown) => {
          console.warn('[attende] open failed:', err);
        });
        break;
      case 'close':
        app?.closePanel();
        break;
      case 'toggle':
        app?.togglePanel();
        break;
      case 'show':
        app?.show();
        break;
      case 'hide':
        app?.hide();
        break;
      case 'identify': {
        const opts = rest[0];
        if (!isIdentifyOptions(opts)) {
          console.warn('[attende] identify: invalid options');
          return;
        }
        app?.identify(opts).catch((err: unknown) => {
          console.warn('[attende] identify failed:', err);
        });
        break;
      }
      case 'shutdown':
        app?.shutdown();
        app = null;
        booting = false;
        break;
      case 'on': {
        const event = rest[0];
        const cb = rest[1];
        if (!isAttendEvent(event) || typeof cb !== 'function') return;
        app?.onEvent(event, cb as (...a: unknown[]) => void);
        break;
      }
      case 'off': {
        const event = rest[0];
        const cb = rest[1];
        if (!isAttendEvent(event) || typeof cb !== 'function') return;
        app?.offEvent(event, cb as (...a: unknown[]) => void);
        break;
      }
      default:
        console.warn('[attende] Unknown command:', cmd);
    }
  }

  // Replace window.attende with real handler
  (window as unknown as Record<string, unknown>)['attende'] = function (...args: unknown[]) {
    processCommand(args);
  };

  // Auto-boot from data-tenant attribute
  const script = document.currentScript as HTMLScriptElement | null;
  if (script) {
    const tenant = script.dataset['tenant'];
    const apiBase = script.dataset['api'];
    if (tenant && !booting && !app) {
      booting = true;
      const opts: { tenant: string; apiBase?: string } = { tenant };
      if (apiBase) opts.apiBase = apiBase;
      app = new AttendeApp(opts);
      app.boot().catch((err: unknown) => {
        console.warn('[attende] Boot failed:', err);
      });
    }
  }

  // A fila roda DEPOIS do auto-boot: comandos como on/open/identify precisam de um widget já criado
  // (antes caíam em `app?.` com app ainda nulo e eram descartados em silêncio).
  queue.forEach(processCommand);

})();
